import React from 'react';

const RENDER_BLOCK = 5;

const RenderBlock = React.memo(({ items, renderItem, block }) => (
  items.slice(block * RENDER_BLOCK, block * RENDER_BLOCK + RENDER_BLOCK).map((item, index) => renderItem(item, index + block * RENDER_BLOCK))
));

const RenderItems = React.memo(({ items, renderItem, firstBlock, lastBlock }) => (
  lastBlock > firstBlock && [...Array(lastBlock - firstBlock)].map((_, i) => <RenderBlock key={i + firstBlock} items={items} renderItem={renderItem} block={i + firstBlock}/>)
));

function locateItem(items, offset, height) {
  if (typeof height !== 'function') {
    return Math.floor(offset / height);
  }
  let pos = 0;
  while (pos < items.length) {
    const ht = height(items[pos]);
    if (offset < ht) return pos;
    offset -= ht;
    pos += 1;
  }
  return pos;
}

export function VirtualList({ containerType="ul", itemSelector="li", items, renderItem, maxHeight, itemHeight=18, onScroll: prevOnScroll, containerStyle, ...props }) {
  const [firstBlock, setFirstBlock] = React.useState(0);

  const scrollRef = React.useRef();

  const lineHeight = React.useRef(itemHeight);
  React.useEffect(() => {
    const line = scrollRef.current.querySelector(itemSelector);
    if (line && typeof lineHeight.current !== 'function') {
      lineHeight.current = line.offsetHeight;
    }
  }, [items, itemSelector]);

  const onScroll = React.useCallback(e => {
    setFirstBlock(Math.max(Math.floor(locateItem(items, scrollRef.current.scrollTop, lineHeight.current) / RENDER_BLOCK) - 1, 0));
    if (prevOnScroll) prevOnScroll(e);
  }, [prevOnScroll, items]);

  const lastBlock = React.useMemo(() => {
    if (typeof lineHeight.current !== 'function') {
      return Math.min(firstBlock + Math.ceil(maxHeight / lineHeight.current / RENDER_BLOCK) + 4, Math.ceil(items.length / RENDER_BLOCK));
    }
    let pos = Math.min(items.length, (firstBlock + 2) * RENDER_BLOCK);
    let remain = maxHeight;
    while (pos < items.length) {
      const ht = lineHeight.current(items[pos]);
      if (remain < ht) break;
      remain -= ht;
      pos += 1;
    }
    return Math.min(Math.ceil(pos / RENDER_BLOCK + 2), Math.ceil(items.length / RENDER_BLOCK));
  }, [items, firstBlock, maxHeight]);
  const renderFirst = firstBlock * RENDER_BLOCK;
  const renderLast = Math.min(lastBlock * RENDER_BLOCK, items.length);

  const [marginTop, marginBottom] = React.useMemo(() => {
    if (typeof lineHeight.current !== 'function') {
      return [renderFirst * lineHeight.current, (items.length - renderLast) * lineHeight.current];
    }
    return [
      items.slice(0, renderFirst).reduce((sum, row) => sum + lineHeight.current(row), 0),
      items.slice(renderLast).reduce((sum, row) => sum + lineHeight.current(row), 0),
    ];
  }, [renderFirst, renderLast, items]);

  const Container = containerType;

  return (
    <div {...props} ref={scrollRef} onScroll={onScroll}>
      <Container style={{marginTop, marginBottom, ...containerStyle}}>
        <RenderItems items={items} renderItem={renderItem} firstBlock={firstBlock} lastBlock={lastBlock}/>
      </Container>
    </div>
  );
}

function RenderItemAuto({ renderItem, item, index, lineHeight, setLineHeight }) {
  const ref = React.useRef();
  React.useLayoutEffect(() => {
    if (!ref.current) return;
    const height = Math.round(ref.current.offsetHeight);
    if (height !== lineHeight[index]) {
      setLineHeight(values => {
        values = values.slice();
        values[index] = height;
        return values;
      });
    }
  }, [lineHeight[index], setLineHeight, index]);
  return renderItem(item, index, ref);
}

const RenderBlockAuto = React.memo(({ items, block, ...props }) => (
  items.slice(block * RENDER_BLOCK, block * RENDER_BLOCK + RENDER_BLOCK).map((item, index) => (
    <RenderItemAuto key={index} item={item} index={index + block * RENDER_BLOCK} {...props}/>
  ))
));

const RenderItemsAuto = React.memo(({ firstBlock, lastBlock, ...props }) => (
  lastBlock > firstBlock && [...Array(lastBlock - firstBlock)].map((_, i) => (
    <RenderBlockAuto key={i + firstBlock} block={i + firstBlock} {...props}/>
  ))
));

function locateItemAuto(offset, height) {
  let pos = 0;
  while (pos < height.length) {
    const ht = height[pos];
    if (offset < ht) return pos;
    offset -= ht;
    pos += 1;
  }
  return pos;
}

export function VirtualListAuto({ containerType="ul", itemSelector="li", items, renderItem, maxHeight, itemHeight=18, onScroll: prevOnScroll, containerStyle, ...props }) {
  const [firstBlock, setFirstBlock] = React.useState(0);

  const [lineHeight, setLineHeight] = React.useState(() => items.map(() => itemHeight));
  React.useEffect(() => {
    if (lineHeight.length !== items.length) {
      while (lineHeight.length < items.length) {
        lineHeight.push(itemHeight);
      }
      lineHeight.length = items.length;
    }
  }, [items.length, lineHeight, itemHeight]);

  const scrollRef = React.useRef();

  const onScroll = React.useCallback(e => {
    setFirstBlock(Math.max(Math.floor(locateItemAuto(scrollRef.current.scrollTop, lineHeight) / RENDER_BLOCK) - 1, 0));
    if (prevOnScroll) prevOnScroll(e);
  }, [prevOnScroll, lineHeight]);

  const lastBlock = React.useMemo(() => {
    let pos = Math.min(lineHeight.length, (firstBlock + 2) * RENDER_BLOCK);
    let remain = maxHeight;
    while (pos < lineHeight.length) {
      const ht = lineHeight[pos];
      if (remain < ht) break;
      remain -= ht;
      pos += 1;
    }
    return Math.min(Math.ceil(pos / RENDER_BLOCK + 2), Math.ceil(items.length / RENDER_BLOCK));
  }, [lineHeight, firstBlock, maxHeight]);
  const renderFirst = firstBlock * RENDER_BLOCK;
  const renderLast = Math.min(lastBlock * RENDER_BLOCK, items.length);

  const [marginTop, marginBottom] = React.useMemo(() => {
    return [
      lineHeight.slice(0, renderFirst).reduce((sum, value) => sum + value, 0),
      lineHeight.slice(renderLast).reduce((sum, value) => sum + value, 0),
    ];
  }, [renderFirst, renderLast, lineHeight]);

  const Container = containerType;

  return (
    <div {...props} ref={scrollRef} onScroll={onScroll}>
      <Container style={{marginTop, marginBottom, ...containerStyle}}>
        <RenderItemsAuto items={items} renderItem={renderItem} firstBlock={firstBlock} lastBlock={lastBlock} lineHeight={lineHeight} setLineHeight={setLineHeight}/>
      </Container>
    </div>
  );
}

export function VirtualListEx({ containerType="ul", itemSelector="li", items, renderItem, itemHeight=18, containerRect, className }) {
  const [viewportTop, setTop] = React.useState(0);
  const [viewportBottom, setBottom] = React.useState(0);
  const [lineHeight, setLineHeight] = React.useState(itemHeight);

  const scrollRef = React.useRef();
  React.useLayoutEffect(() => {
    const line = scrollRef.current.querySelector(itemSelector);
    if (line) setLineHeight(line.offsetHeight);
  }, [items, itemSelector]);

  React.useLayoutEffect(() => {
    if (!scrollRef.current || !containerRect) return;
    const innerRect = scrollRef.current.getBoundingClientRect();
    const top = -(innerRect.top - containerRect.top);
    setTop(top);
    setBottom(top + containerRect.height);
  }, [containerRect, items]);

  const firstItem = Math.max(0, Math.min(Math.floor(viewportTop / lineHeight), items.length - 1));
  const lastItem = Math.max(0, Math.min(Math.ceil((viewportBottom + Math.max(0, -viewportTop)) / lineHeight), items.length));
  const firstBlock = Math.floor(firstItem / RENDER_BLOCK) - 1;
  const lastBlock = Math.ceil(lastItem / RENDER_BLOCK) + 1;
  const renderFirst = Math.max(0, firstBlock * RENDER_BLOCK);
  const renderLast = Math.min(lastBlock * RENDER_BLOCK, items.length);

  const Container = containerType;

  return (
    <Container ref={scrollRef} className={className} style={{paddingTop: renderFirst * lineHeight, paddingBottom: (items.length - renderLast) * lineHeight}}>
      <RenderItems items={items} renderItem={renderItem} firstBlock={firstBlock} lastBlock={lastBlock}/>
    </Container>
  );
}
